import {useId, forwardRef, Ref, ChangeEvent, useMemo} from 'react';
import './flat-multi-text-select.scss';
import {
  Autocomplete,
  Box,
  FormControl,
  Slide,
  SxProps,
  useTheme,
  Paper,
  Typography,
  Avatar,
} from '@mui/material';
import {Theme} from '@mui/material/styles';
import {Pallete} from '../../theme/bluebird';
import {CustomCheckbox} from '../checkbox/checkbox';
import FlatInput from '../flat-input/flat-input';
import ClearIcon from '@mui/icons-material/Clear';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';

export interface Option {
  key: string;
  label: string;
  picture?: string;
}

export interface FlatMultiTextSelectProps {
  id?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  errors?: string;
  options: Option[];
  value: Option[];
  multiple?: boolean;
  placeholder?: string;
  readonly?: boolean;
  loading?: boolean;
  onChange: (selected: Option | Option[]) => void;
  hasPictureTag?: boolean;
  sx?: SxProps<Theme>;
}

export const FlatMultiTextSelect = forwardRef(
  (
    {
      label,
      required,
      disabled,
      disabledTooltip,
      errors,
      options,
      sx,
      value,
      onChange,
      placeholder,
      multiple = true,
      readonly,
      hasPictureTag,
      ...rest
    }: FlatMultiTextSelectProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const hasError = !!errors;
    const id = useId();
    const uniqueId = rest.id ?? `flat_multi_text_select_${id}`;

    const handleChange = (_event: ChangeEvent<{}>, selected: Option | Option[]) =>
      onChange(Array.isArray(selected) ? selected : [selected]);

    const val = useMemo(() => {
      if (!value) {
        if (multiple) return [];
        return null;
      }
      if (!multiple) return value[0];
      return value;
    }, [multiple, value]);

    return (
      <FormControl
        className={`flat-multi-text-select ${disabled ? 'disabled' : ''}`}
        sx={{width: '100%'}}
      >
        {label && (
          <Box className="label-container">
            <Typography component="label" htmlFor={uniqueId} noWrap>
              {label}
            </Typography>
            {required && !disabled && <Typography component="span">*</Typography>}
          </Box>
        )}
        <Autocomplete
          ref={ref}
          {...rest}
          /* @ts-ignore */
          value={val}
          onChange={handleChange}
          multiple={multiple}
          disableListWrap
          disableClearable
          disableCloseOnSelect={multiple}
          readOnly={readonly}
          disabled={disabled}
          options={options}
          loadingText={t(translations.loading_content.loading)}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          getOptionLabel={!multiple ? (option) => option.label : undefined}
          renderTags={
            multiple
              ? (value: Option[], getTagProps) => {
                  return (
                    <Box
                      className="display-text"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flexWrap: 'nowrap',
                        pointerEvents: disabled ? 'none' : 'all',
                        display: 'flex',
                        gap: hasPictureTag ? '6px' : 'unset',
                      }}
                    >
                      {value.map(({key, label, picture}: Option, index) => {
                        // Ignore the `onDelete` prop as it has no effect in the span element.
                        const {onDelete, ...tagProps} = getTagProps({index});
                        return (
                          <Box
                            key={key}
                            sx={{
                              backgroundColor: hasPictureTag ? '#E2ECFF' : 'unset',
                              paddingX: hasPictureTag ? '4px' : 'unset',
                              borderRadius: '5px',
                              display: 'flex',
                              '& .MuiAutocomplete-tag': {
                                alignItems: 'center',
                              },
                            }}
                          >
                            <span
                              {...tagProps}
                              style={{
                                display: 'flex',
                                gap: hasPictureTag ? '4px' : 'unset',
                                color: Pallete.gray.base,
                                pointerEvents: disabled ? 'none' : 'all',
                              }}
                            >
                              {hasPictureTag && (
                                <Avatar
                                  alt={label}
                                  src={picture}
                                  sx={{
                                    height: '22px',
                                    width: '22px',
                                  }}
                                >
                                  {label[0]}
                                </Avatar>
                              )}
                              {label}
                              {hasPictureTag && (
                                <ClearIcon
                                  sx={{
                                    fontSize: '16px',
                                    height: '100%',
                                    zIndex: 1,
                                    cursor: 'pointer',
                                  }}
                                  onClick={(evt) => onDelete(evt)}
                                />
                              )}
                              {index !== value.length - 1 && !hasPictureTag && ','}
                            </span>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                }
              : undefined
          }
          renderOption={(props, option, state) => {
            return (
              <li
                {...props}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '6px',
                  color: state.selected ? theme.palette.primary.main : '',
                  margin: 0,
                  padding: '10px 8px',
                }}
              >
                <CustomCheckbox checked={state.selected} sx={{padding: 0}} />
                <span
                  style={{
                    fontWeight: state.selected ? 500 : 400,
                    fontSize: '14px',
                    color: Pallete.gray.base,
                    display: 'flex',
                    gap: '6px',
                  }}
                >
                  {hasPictureTag && (
                    <Avatar
                      sx={{
                        height: '22px',
                        width: '22px',
                        marginLeft: '2px',
                      }}
                      src={option.picture}
                      alt={option.label}
                    >
                      {option.label[0]}
                    </Avatar>
                  )}
                  {option.label}
                </span>
              </li>
            );
          }}
          renderInput={(params) => {
            return (
              <FlatInput
                {...params}
                ref={params.InputProps.ref}
                disabled={disabled}
                disabledTooltip={disabledTooltip}
                readonly={readonly}
                placeholder={!value || value.length === 0 ? placeholder : undefined}
                InputLabelProps={{children: null}}
                // errors={errors}
                sx={{
                  caretColor: 'transparent',
                  display: 'flex',
                  justifyContent: 'center',
                  '& fieldset': {border: 'none'},
                  '& input': {
                    position: 'absolute',
                    width: '84% !important',
                  },
                  '.MuiFilledInput-root': {
                    padding: '0px 12px',
                    height: '42px',
                    backgroundColor: readonly ? Pallete.white.darker : '',
                  },
                  '&:focus, &:focus-within, &:hover': {
                    '.display-text': {
                      visibility:
                        // @ts-ignore
                        Number(params.inputProps.value.length) === 0 ? 'visible' : 'hidden',
                    },
                  },
                }}
              />
            );
          }}
          PaperComponent={(props) => (
            // @ts-ignore
            <Paper
              component="div"
              elevation={0}
              {...props}
              sx={{
                borderRadius: '5px!important',
                boxShadow:
                  '0px 1px 2px rgba(60, 64, 67, 0.3), 0px 1px 3px 1px rgba(60, 64, 67, 0.15)',
              }}
            />
          )}
          sx={{
            height: '42px',
            overflow: 'hidden',
            borderRadius: '5px 5px 0 0',
            lineHeight: 0,
            padding: 0,
            ...sx,
            '.MuiAutocomplete-inputRoot': {
              pointerEvents: disabled ? 'none' : 'all',
              paddingTop: disabled ? 0 : 'inherit',
            },
          }}
        />
        <Slide direction="left" in={hasError} mountOnEnter unmountOnExit>
          <span className="input-error" style={{color: Pallete.red.base}}>
            {errors}
          </span>
        </Slide>
      </FormControl>
    );
  }
);
